import { useAuth } from "@/auth";
import { PRO_BUYER_RESERVATIONS_ENABLED } from "@/config";
import {
  BuyerReservation,
  EMPTY_PRODUCT_RESERVATION,
  ProductReservationState,
  ReservationMutationResult,
  SellerReservation,
  acceptReservation,
  declineReservation,
  fetchMyReservations,
  fetchProductReservation,
  fetchSellerReservations,
  releaseReservation,
  requestReservation,
} from "@/features/pro-buyer-reservations/data/reservations-client";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * WHO-5947. Every key starts here and carries the viewer id: the fields are
 * viewer-scoped, so one viewer's answer is never served to another.
 */
export const RESERVATIONS_QUERY_KEY = ["pro-buyer-reservations"] as const;

type ReservationActions = {
  request: (productId: string) => Promise<ReservationMutationResult>;
  release: (id: string) => Promise<ReservationMutationResult>;
  accept: (id: string) => Promise<ReservationMutationResult>;
  decline: (id: string) => Promise<ReservationMutationResult>;
};

const useProductReservationLive = (productId: string | null | undefined) => {
  const { token, isLoggedIn, isReady, userId } = useAuth();
  const viewerToken = isLoggedIn && token ? token : null;

  const query = useQuery({
    queryKey: [...RESERVATIONS_QUERY_KEY, "product", userId ?? null, productId],
    queryFn: () => fetchProductReservation(viewerToken, productId ?? ""),
    enabled: isReady && !!productId,
  });

  return query.data ?? EMPTY_PRODUCT_RESERVATION;
};

const useMyReservationsLive = (): BuyerReservation[] => {
  const { token, isLoggedIn, userId } = useAuth();
  const enabled = isLoggedIn && !!token;

  const query = useQuery({
    queryKey: [...RESERVATIONS_QUERY_KEY, "mine", userId],
    queryFn: () => fetchMyReservations(token),
    enabled,
  });

  return enabled ? query.data ?? [] : [];
};

const useSellerReservationsLive = (): SellerReservation[] => {
  const { token, isLoggedIn, userId } = useAuth();
  const enabled = isLoggedIn && !!token;

  const query = useQuery({
    queryKey: [...RESERVATIONS_QUERY_KEY, "seller", userId],
    queryFn: () => fetchSellerReservations(token),
    enabled,
  });

  return enabled ? query.data ?? [] : [];
};

/** Mutations; every success refetches all reservation reads of this viewer. */
/**
 * WHO-5947 F22. The gateway reads reservations from a read replica, so a
 * refetch straight after a transition can return the OLD row: the first
 * Accept looked like it did nothing (the list refetched to REQUESTED) and a
 * second click was needed. The mutation's own answer is authoritative, so it
 * goes into every cached read first, and the background refetch waits until
 * the replica has caught up.
 */
export const RESERVATION_REFETCH_DELAY_MS = 4000;

const LIVE_STATUSES = new Set(["REQUESTED", "ACCEPTED"]);

/**
 * WHO-5947 F30. The product page state after the buyer's own transition.
 * Releasing an accepted reservation also ends the public hold: keeping
 * `reservedUntilDate` showed the buyer "reserved for another buyer" until a
 * reload. Releasing or withdrawing starts the 7-day cooldown, which the
 * next eligibility read will report too.
 */
export const nextProductReservationState = (
  state: ProductReservationState,
  row: BuyerReservation | SellerReservation
): ProductReservationState => {
  if (LIVE_STATUSES.has(row.status)) {
    return {
      ...state,
      myReservation: { fixedPrice: null, ...state.myReservation, ...row },
    };
  }

  const wasMine = state.myReservation?.id === row.id;
  const endedMyHold = wasMine && state.myReservation?.status === "ACCEPTED";

  return {
    ...state,
    myReservation: null,
    reservedUntilDate: endedMyHold ? null : state.reservedUntilDate,
    eligibility:
      wasMine && row.status === "RELEASED" && state.eligibility
        ? { ...state.eligibility, allowed: false, reason: "COOLDOWN" }
        : state.eligibility,
  };
};

export const applyReservationTransition = (
  queryClient: QueryClient,
  row: SellerReservation | BuyerReservation,
  side: "buyer" | "seller"
) => {
  const isLive = LIVE_STATUSES.has(row.status);

  const updateList = <TRow extends SellerReservation | BuyerReservation>(
    rows: TRow[] | undefined
  ) => {
    if (!rows) {
      return rows;
    }

    const next = rows
      // Accepting one request declines every other request on that listing.
      .filter(
        existing =>
          !(
            row.status === "ACCEPTED" &&
            existing.id !== row.id &&
            existing.productId === row.productId &&
            existing.status === "REQUESTED"
          )
      )
      .map(existing =>
        existing.id === row.id ? ({ ...existing, ...row } as TRow) : existing
      );

    return isLive ? next : next.filter(existing => existing.id !== row.id);
  };

  queryClient.setQueriesData<SellerReservation[]>(
    { queryKey: [...RESERVATIONS_QUERY_KEY, "seller"] },
    updateList
  );
  queryClient.setQueriesData<BuyerReservation[]>(
    { queryKey: [...RESERVATIONS_QUERY_KEY, "mine"] },
    updateList
  );

  if (side === "seller") {
    return;
  }

  // Product reads are keyed [..., "product", viewerId, productId].
  queryClient
    .getQueriesData<ProductReservationState>({
      queryKey: [...RESERVATIONS_QUERY_KEY, "product"],
    })
    .forEach(([queryKey, state]) => {
      if (!state || queryKey[3] !== row.productId) {
        return;
      }

      queryClient.setQueryData<ProductReservationState>(
        queryKey,
        nextProductReservationState(state, row)
      );
    });
};

const useReservationActionsLive = (): ReservationActions => {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const run =
    <TArg>(
      mutation: (
        token: string | null,
        arg: TArg
      ) => Promise<ReservationMutationResult>,
      side: "buyer" | "seller"
    ) =>
    async (arg: TArg) => {
      const result = await mutation(token, arg);

      if (result.ok && result.reservation) {
        applyReservationTransition(queryClient, result.reservation, side);
        setTimeout(
          () =>
            void queryClient.invalidateQueries({
              queryKey: RESERVATIONS_QUERY_KEY,
            }),
          RESERVATION_REFETCH_DELAY_MS
        );

        return result;
      }

      await queryClient.invalidateQueries({ queryKey: RESERVATIONS_QUERY_KEY });

      return result;
    };

  return {
    request: run(requestReservation, "buyer"),
    release: run(releaseReservation, "buyer"),
    accept: run(acceptReservation, "seller"),
    decline: run(declineReservation, "seller"),
  };
};

const inertActions: ReservationActions = {
  request: async () => ({ ok: false, code: "FEATURE_DISABLED" }),
  release: async () => ({ ok: false, code: "FEATURE_DISABLED" }),
  accept: async () => ({ ok: false, code: "FEATURE_DISABLED" }),
  decline: async () => ({ ok: false, code: "FEATURE_DISABLED" }),
};

/*
 * The flag is a build-time constant, so each export is fixed for the lifetime
 * of the bundle: with the flag off every surface gets an inert hook that
 * sends no request and needs no QueryClient (hook order never changes).
 */
export const useProductReservation: (
  productId: string | null | undefined
) => ProductReservationState = PRO_BUYER_RESERVATIONS_ENABLED
  ? useProductReservationLive
  : () => EMPTY_PRODUCT_RESERVATION;

export const useMyReservations: () => BuyerReservation[] =
  PRO_BUYER_RESERVATIONS_ENABLED ? useMyReservationsLive : () => [];

export const useSellerReservations: () => SellerReservation[] =
  PRO_BUYER_RESERVATIONS_ENABLED ? useSellerReservationsLive : () => [];

export const useReservationActions: () => ReservationActions =
  PRO_BUYER_RESERVATIONS_ENABLED
    ? useReservationActionsLive
    : () => inertActions;
